import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useHotkeys } from "react-hotkeys-hook";
import { ArrowLeftRight, Hash, Search, X } from "lucide-react";
import {
  COMMAND_PAGES,
  handleMatchesQuery,
} from "../../../utils/constants/CommandPalette";
import { handleGetAppLogo } from "../../../utils/constants/GlobalAppAndLanguage";
import { isAgencyScopedUser } from "../../../utils/agencyScope";
import useAmsConfig from "../../../hooks/useAmsConfig";
import useAppSwitchNavigation from "../../../hooks/useAppSwitchNavigation";
import useGlobalAppAndLanguage from "../../../hooks/useGlobalAppAndLanguage";
import usePermissions from "../../../hooks/usePermissions";
import useZustandStore from "../../../zustandStore/useZustandStore";
import CommandPaletteRow from "./CommandPaletteRow";

/** Cmd/Ctrl+K palette: jump to a page, look up a Parent Ad ID or topic, or switch app. */
const CommandPalette = () => {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const open = useZustandStore((s) => s.openCommandPalette);
  const setOpen = useZustandStore((s) => s.setOpenCommandPalette);
  const currentUser = useZustandStore((s) => s?.currentUser);
  const { hasNavLinkAccess } = usePermissions();
  const { appNames } = useAmsConfig();
  const {
    globalApp,
    globalLabel,
    lastLanguageByApp,
    handleGlobalLabel,
    handleResolveLanguage,
  } = useGlobalAppAndLanguage();
  const { handleSwitchAndNavigate } = useAppSwitchNavigation();
  const isLoggedIn = !!currentUser?.roles;
  const agencyScoped = isAgencyScopedUser(currentUser);
  const text = query.trim();

  const pages = useMemo(
    () =>
      COMMAND_PAGES.map((p) => ({
        ...p,
        path: agencyScoped && p.agencyPath ? p.agencyPath : p.path,
      })).filter(
        (p) => !(agencyScoped && p.hideForAgency) && hasNavLinkAccess(p.path)
      ),
    [agencyScoped, hasNavLinkAccess]
  );

  const sections = useMemo(() => {
    const goTo = (path) => () => navigate(path);
    const lookups = text
      ? pages
          .filter((p) => p.lookup)
          .map((p) => ({
            key: `find-${p.path}`,
            icon: /^\d+$/.test(text) ? Hash : Search,
            label: /^\d+$/.test(text)
              ? `Parent Ad ID ${text} in ${p.label}`
              : `“${text}” in ${p.label}`,
            hint: "Look up",
            run: goTo(`${p.path}?query=${encodeURIComponent(text)}`),
          }))
      : [];
    const pageItems = pages
      .filter(
        (p) =>
          !text ||
          handleMatchesQuery({
            query: text,
            text: `${p.label} ${p.group} ${p.keywords || ""}`,
          })
      )
      .map((p) => ({
        key: `page-${p.path}`,
        icon: p.icon,
        label: p.label,
        hint: p.group,
        run: goTo(p.path),
      }));
    const switchItems = appNames
      .filter((a) => a.value !== globalApp)
      .map((a) => {
        const language = handleResolveLanguage({
          app: a.value,
          language: lastLanguageByApp[a.value],
        });
        return {
          key: `switch-${a.value}`,
          logo: handleGetAppLogo({ app: a.value }),
          label: `Switch to ${handleGlobalLabel({ app: a.value, language })}`,
          hint: "App",
          run: () => handleSwitchAndNavigate({ app: a.value, language }),
        };
      })
      .filter(
        (item) =>
          !text ||
          handleMatchesQuery({ query: text, text: `${item.label} app` })
      );
    return [
      { title: `Look up in ${globalLabel}`, items: lookups },
      { title: text ? "Pages" : "Jump to", items: pageItems },
      { title: "Switch app", items: switchItems },
    ].filter((s) => s.items.length > 0);
  }, [
    text,
    pages,
    appNames,
    globalApp,
    globalLabel,
    lastLanguageByApp,
    handleGlobalLabel,
    handleResolveLanguage,
    handleSwitchAndNavigate,
    navigate,
  ]);

  const items = sections.flatMap((s) => s.items);

  useHotkeys("mod+k", () => setOpen(true), {
    preventDefault: true,
    enableOnFormTags: true,
  });

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setSelected(0);
    inputRef.current?.focus();
  }, [open]);

  if (!isLoggedIn || !open) return null;

  const handleClose = () => setOpen(false);

  const handleRun = ({ item }) => {
    if (!item) return;
    handleClose();
    item.run();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      handleClose();
    } else if (e.key === "ArrowDown" && items.length) {
      e.preventDefault();
      setSelected((s) => (s + 1) % items.length);
    } else if (e.key === "ArrowUp" && items.length) {
      e.preventDefault();
      setSelected((s) => (s - 1 + items.length) % items.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      handleRun({ item: items[selected] });
    }
  };

  let index = -1;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      onMouseDown={handleClose}
      className="fixed inset-0 z-[10000] flex items-start justify-center bg-[#1c1638]/25 px-4 pt-[12vh] backdrop-blur-[3px]"
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        className="w-full max-w-[680px] overflow-hidden rounded-2xl bg-white shadow-[0_30px_80px_rgba(28,22,56,0.28)] ring-1 ring-black/5"
      >
        <div className="flex items-center gap-3 border-b border-slate-100 px-5 py-4">
          <Search className="h-5 w-5 shrink-0 text-[#7f7ad5]" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelected(0);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Search pages, or type a Parent Ad ID or topic…"
            className="min-w-0 flex-1 bg-transparent text-[15px] text-slate-800 outline-none placeholder:text-slate-400"
          />
          <span className="hidden shrink-0 items-center gap-1.5 rounded-full bg-[#f1effc] py-0.5 pl-0.5 pr-2.5 text-xs font-medium text-[#3c3489] sm:flex">
            <img
              src={handleGetAppLogo({ app: globalApp })}
              alt=""
              className="h-5 w-5 rounded-full bg-white object-cover"
            />
            {globalLabel}
          </span>
          <button
            type="button"
            aria-label="Close"
            onClick={handleClose}
            className="rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-[56vh] overflow-y-auto px-2 py-2" id="HideScroll">
          {sections.length === 0 && (
            <p className="px-4 py-10 text-center text-sm text-slate-400">
              Nothing matches “{text}”.
            </p>
          )}
          {sections.map((section) => (
            <div key={section.title} className="py-1">
              <p className="flex items-center gap-1.5 px-3 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wider text-slate-400">
                {section.title === "Switch app" && (
                  <ArrowLeftRight className="h-3 w-3" />
                )}
                {section.title}
              </p>
              {section.items.map((item) => {
                index += 1;
                const rowIndex = index;
                return (
                  <CommandPaletteRow
                    key={item.key}
                    item={item}
                    isSelected={rowIndex === selected}
                    onHover={() => setSelected(rowIndex)}
                    onRun={() => handleRun({ item })}
                  />
                );
              })}
            </div>
          ))}
        </div>

        <div className="flex items-center gap-5 border-t border-slate-100 bg-slate-50/70 px-5 py-2.5 text-xs text-slate-500">
          {[
            ["Select", ["↑", "↓"]],
            ["Open", ["↵"]],
            ["Close", ["esc"]],
          ].map(([label, keys]) => (
            <span key={label} className="flex items-center gap-1.5">
              {label}
              {keys.map((k) => (
                <kbd
                  key={k}
                  className="min-w-[20px] rounded-md border border-slate-200 bg-white px-1.5 py-px text-center font-sans text-[11px] text-slate-500 shadow-[0_1px_0_rgba(0,0,0,0.04)]"
                >
                  {k}
                </kbd>
              ))}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
